import * as angular from 'angular';
import * as _ from 'lodash';
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsDisplayEnums} from "../../shared/dto/enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsCommon} from "../../shared/dto/aps.common";

export interface RoleLocation {
  role: apsCommon.UserRolePlants;
  plant: apsCommon.IdAndName;
  shop: apsCommon.IdAndName;
  hasFlag(flag: apsEnums.RoleFlags): boolean;
}

export interface AuthDataService {
  getToken(): string;
  setToken(newToken: string): void;
  clearToken(): void;
  getRoles(): apsCommon.UserRolePlants[];
  setRoles(roles: apsCommon.UserRolePlants[]): void;
  getSelectedRoleLocation(): RoleLocation;
  setSelectedRoleLocation(role: apsCommon.UserRolePlants,
                          plant?: apsCommon.IdAndName,
                          shop?: apsCommon.IdAndName): void;
  clearSelectedRoleLocation(): void;
  getUserData(): apsViewModels.UserData;
  setUserData(userName: apsViewModels.UserData): void;
  clearUserData(): void;

  isAdmin(): boolean;
  isPlantAdmin(plantId: string): boolean;
  isPlantAdminForCurrentPlant(): boolean;
  isAnyPlantAdmin(): boolean;
  isAPSIOAdminForPlant(plantId: string): boolean;
  isAPSIOAdminForCurrentPlant(): boolean;
  isAPSIOAdmin(): boolean;
  isPlantOrAPSIOAdmin(): boolean;
  isPlantOrAPSIOAdminForPlant(plantId: string): boolean;
  isPlantOrAPSIOAdminForCurrentPlant(): boolean;
  isAuthorizedForPlant(plantId: string): boolean;
  isAuthorizedForCurrentPlant(): boolean;
  setUserId(userId: string): void;
  getUserId(): string;
  clearUserId(): void;
  isHEUpkeep(): boolean;
  canDeleteWeights(): boolean;
  isHEMachineOperator(): boolean;
  isOperator(): boolean;
  isScaleMode(): boolean;
  hasFlag(flag: apsEnums.RoleFlags): boolean;
  isHotEndOperator(): boolean;
  isHotEndUpkeep(): boolean;
  isColdEndOperator(): boolean;
  isColdEndInspector(): boolean;
  isAuthorizedForToolChanges(): boolean;
  isAuthorizedForToolChangesComment(): boolean;
  isAuthorizedForDefects(): boolean;
  isAuthorizedForDowntime(): boolean;
  isAuthorizedForDowntimeByRole(): boolean;
  isAuthorizedForQualifications(): boolean;
  isProductionShiftReport(): boolean;
  isKpiAudit(): boolean;
  isHotEndBonusReport(): boolean;
  isJobChangeSupervisor(plantId: string): boolean;
  isAuthorizedForHourlyDowntime(): boolean;
  isAuthorizedForBatchSummary(): boolean;

  getCountInterval(): apsDisplayEnums.CountDisplayInterval;
  setCountInterval(countInterval: apsDisplayEnums.CountDisplayInterval): void;
}

export default angular.module('aps.services.authData', [])
  .service('authDataService', function ($window: angular.IWindowService) {
    var self = <AuthDataService>this;

    var authTokenKey = '-authToken';
    var authRolesKey = '-authRoles';
    var countIntervalKey = '-countInterval';

    var currentUserId: string;
    var currentRoleLocation: RoleLocation;

    let DOWNTIME_ENTRY_ALLOWED_ROLE_IDS:any[] = 
      [{ roleName: 'Batch & Furnace Supervisor', roleId: 'BBDF0000-3A00-000D-9E9E-08D2E5F92C1A' }, 
      { roleName: 'CE Inspector', roleId: 'BBDF0000-3A00-000D-A1C4-08D2E5F92BF0' }, 
      { roleName: 'CE Shift Mechanic', roleId: 'BBDF0000-3A00-000D-2A81-08D2E5F92BFF' }, 
      { roleName: 'Facilities Maintenance Suprvisor', roleId: 'BBDF0000-3A00-000D-B33E-08D2E5F92C0A' }, 
      { roleName: 'Finished Ware Manager', roleId: 'BBDF0000-3A00-000D-3760-08D2E5F92C2F' }, 
      { roleName: 'Machine Operator', roleId: 'BBDF0000-3A00-000D-EE2D-08D2E5F92BE2' }, 
      { roleName: 'Packed Ware Inspector', roleId: 'BBDF0000-3A00-000D-D0D6-08D2E5F92C14' }, 
      { roleName: 'Production Clerk', roleId: 'BBDF0000-3A00-000D-7769-08D2E5F92C10' }, 
      { roleName: 'Quality Control Manager', roleId: 'BBDF0000-3A00-000D-7E8F-08D2E5F92C5E' }, 
      { roleName: 'Selecting Maintenance Supervisor', roleId: 'BBDF0000-3A00-000D-5F9C-08D2E5F92C23' }, 
      { roleName: 'Selecting Shift Supervisor', roleId: 'BBDF0000-3A00-000D-5B5A-08D2E5F92BF9' }, 
      { roleName: 'Selecting Tank Manager', roleId: 'BBDF0000-3A00-000D-4CD5-08D2E5F92C26' }]; 
      
    let HOURLY_DOWNTIME_ROLE_IDS:any[] = 
    [{ roleName: 'Machine Operator', roleId: 'BBDF0000-3A00-000D-EE2D-08D2E5F92BE2' }, 
    { roleName: 'Machine Upkeep', roleId: 'BBDF0000-3A00-000D-184E-08D2E5F92BE6' }, 
    { roleName: 'EH&S Manager', roleId: 'EFFC0000-FF68-0003-1553-08D6B90C1DF6' }]; 

    self.setUserId = function (userId: string): void {
      currentUserId = userId;
    };

    self.getUserId = function () {
      return currentUserId;
    };

    self.clearUserId = function () {
      currentUserId = null;
    };

    self.getToken = function (): string {
      var userId = self.getUserId();
      if (!userId) {
        return null;
      }
      return $window.localStorage[userId + authTokenKey];
    };

    self.setToken = function (newToken: string): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage[userId + authTokenKey] = newToken;
    };

    self.clearToken = function (): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage.removeItem(userId + authTokenKey);
      $window.localStorage.removeItem(userId + authRolesKey);
      self.clearSelectedRoleLocation();
      self.clearUserData();
    };

    self.getRoles = function (): apsCommon.UserRolePlants[] {
      var userId = self.getUserId();
      if (!userId) {
        return [];
      }
      var data = $window.localStorage[userId + authRolesKey];
      if (data) {
        return JSON.parse(data);
      }
      return [];
    };

    self.setRoles = function (roles: apsCommon.UserRolePlants[]): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage[userId + authRolesKey] = JSON.stringify(roles);
      if (roles.length === 1 && !roles[0].roleRequiresPlants) {
        self.setSelectedRoleLocation(roles[0]);
      } else {
        self.clearSelectedRoleLocation();
      }
    };

    function addHasFlag(roleLocation: RoleLocation): void {
      roleLocation.hasFlag = (flag: apsEnums.RoleFlags) => {
        return roleLocation.role &&
          /* tslint:disable:no-bitwise */
          ((roleLocation.role.roleFlags & flag) === flag);
          /* tslint:enable:no-bitwise */
      };
    }

    var selectedRoleLocationKey = '-selectedRoleLocation';
    self.getSelectedRoleLocation = function (): RoleLocation {
      if (currentRoleLocation) {
        return currentRoleLocation;
      }

      var userId = self.getUserId();
      if (!userId) {
        return undefined;
      }
      var data = $window.localStorage[userId + selectedRoleLocationKey];
      if (data) {
        var roleLocation = <RoleLocation>JSON.parse(data);
        addHasFlag(roleLocation);
        currentRoleLocation = roleLocation;
        return currentRoleLocation;
      }
      return undefined;
    };

    self.setSelectedRoleLocation = function (role: apsCommon.UserRolePlants,
                                             plant: apsCommon.IdAndName,
                                             shop: apsCommon.IdAndName): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      currentRoleLocation = {
        role: role,
        plant: plant,
        shop: shop,
        hasFlag: null
      };
      addHasFlag(currentRoleLocation);
      $window.localStorage[userId + selectedRoleLocationKey] =
        JSON.stringify(currentRoleLocation);
    };

    self.clearSelectedRoleLocation = function (): void {
      currentRoleLocation = null;
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage.removeItem(userId + selectedRoleLocationKey);
    };

    var userDataKey = '-userData';
    self.setUserData = function (data: apsViewModels.UserData): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage[userId + userDataKey] = JSON.stringify(data);
    };

    self.getUserData = function (): apsViewModels.UserData {
      var userId = self.getUserId();
      if (!userId) {
        return undefined;
      }
      var data = $window.localStorage[userId + userDataKey];
      if (data) {
        return JSON.parse(data);
      }
      return undefined;
    };

    self.clearUserData = function (): void {
      var userId = self.getUserId();
      if (!userId) {
        return;
      }
      $window.localStorage.removeItem(userId + userDataKey);
    };

    self.isAdmin = function () {
      var userData = self.getUserData();
      return userData.isSysAdmin;
    };

    self.isPlantAdmin = function (plantId: string) {
      var userData = self.getUserData();
      return userData.isSysAdmin
        || userData.plantAdmins && _.includes(userData.plantAdmins, plantId);
    };

    self.isAnyPlantAdmin = function () {
      var userData = self.getUserData();
      return userData.isSysAdmin
        || userData.plantAdmins && userData.plantAdmins.length > 0;
    };

    self.isPlantAdminForCurrentPlant = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isAdmin() || loc.plant && self.isPlantAdmin(loc.plant.id);
    };

    self.isAPSIOAdmin = () => {
      let loc = self.getSelectedRoleLocation();
      return loc.hasFlag(apsEnums.RoleFlags.APSIOAdmin);
    };

    self.isPlantOrAPSIOAdmin = () => self.isAPSIOAdmin() || self.isAnyPlantAdmin();

    self.isPlantOrAPSIOAdminForPlant = plantId =>
      self.isAPSIOAdminForPlant(plantId) || self.isPlantAdmin(plantId);

    self.isPlantOrAPSIOAdminForCurrentPlant = () =>
      self.isAPSIOAdminForCurrentPlant() || self.isPlantAdminForCurrentPlant();

    self.isAPSIOAdminForPlant = function (plantId: string) {
      if (!self.isAuthorizedForPlant(plantId)) {
        return false;
      }

      return self.isAPSIOAdmin();
    };

    self.isAPSIOAdminForCurrentPlant = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isAPSIOAdminForPlant(loc.plant.id);
    };

    self.isAuthorizedForPlant = function (plantId: string) {
      if (self.isPlantAdmin(plantId)) {
        return true;
      }
      var loc = self.getSelectedRoleLocation();
      return !loc.role.roleRequiresPlants || _.some(loc.role.plants, {id: plantId});
    };

    self.isAuthorizedForCurrentPlant = function () {
      var loc = self.getSelectedRoleLocation();
      return !loc.role.roleRequiresPlants || (loc.plant && self.isAuthorizedForPlant(loc.plant.id));
    };


    self.canDeleteWeights = function () {
      var loc = self.getSelectedRoleLocation();
      /*hardcode alert */
      if (loc.role.roleName === "APSIO Administrator" || 
      loc.role.roleName === "Quality Control Manager" ||
      loc.role.roleName === "Production Superintendent"
      ) {
         return true;
      }else {
         return false;
      }
    };
    self.isHEUpkeep = function () {
      var loc = self.getSelectedRoleLocation();
      /*hardcode alert */
      if (loc.role.roleName === "Machine Upkeep") {
         return true;
      }else {
         return false;
      }
    };

    self.isHEMachineOperator = function () {
      var loc = self.getSelectedRoleLocation();
      /*hardcode alert */
      if (loc.role.roleName === "Machine Operator") {
         return true;
      }else {
         return false;
      }
    };

    
    self.isJobChangeSupervisor = function (plantId: string) {
      if (!self.isAuthorizedForPlant(plantId)) {
        return false;
      }

      if(self.isAPSIOAdmin()){
        return true;
      }

      if(self.isPlantAdmin(plantId)){
        return true;
      }

      var loc = self.getSelectedRoleLocation();
      /*hardcode alert */
      if (loc.role.roleName === "Job Change Supervisor") {
         return true;
      }else {
         return false;
      }

    };

    self.isProductionShiftReport = function () {
       if (_.find(self.getRoles(), x => x.roleName=='Production Shift Report')) {
        return true;
      }
      
      return false;
    };
    self.isKpiAudit = function () {
      if (_.find(self.getRoles(), x => x.roleName=='KPI Auditor')) {
        return true;
      }
      
      return false;
    };
    self.isHotEndBonusReport = function () {
       if (_.find(self.getRoles(), x => x.roleName=='Hot End Bonus Report')) {
        return true;
      }
      
      return false;
    };
    self.isScaleMode = function () {
      return _.some(self.getRoles(), function(r) {
        /* tslint:disable:no-bitwise */
        return ((r.roleFlags & apsEnums.RoleFlags.ScaleMode) === apsEnums.RoleFlags.ScaleMode);
        /* tslint:enable:no-bitwise */
      });
    };

    self.hasFlag = function (flag: apsEnums.RoleFlags) {
      var loc = self.getSelectedRoleLocation();
      return self.isAuthorizedForCurrentPlant() && loc.hasFlag(flag);
    };

    self.isOperator = function () {
      return self.hasFlag(apsEnums.RoleFlags.Operator);
    };

    self.isHotEndUpkeep = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isOperator() && loc.role.roleLocation === apsEnums.Location.HotEnd;
    };

    self.isHotEndOperator = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isOperator() && loc.role.roleLocation === apsEnums.Location.HotEnd;
    };

    self.isColdEndOperator = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isOperator() && loc.role.roleLocation === apsEnums.Location.ColdEnd;
    };

    self.isColdEndInspector = function () {
      return self.hasFlag(apsEnums.RoleFlags.ColdEndInspector);
    };

    self.isAuthorizedForToolChanges = function () {
      return self.isHotEndOperator();
    };

    self.isAuthorizedForToolChangesComment = function () {
      return self.isColdEndInspector();
    };

    self.isAuthorizedForDefects = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isAdmin() || loc.role.roleFlags > 0;
    };

    var isAuthorizedByRoleList = function(roleList: {roleId: string, roleName: string}[]) {
      var loc = self.getSelectedRoleLocation();

      return (_.filter(roleList, 
            (n) => { 
              return n.roleId === loc.role.roleId.toUpperCase() && n.roleName == loc.role.roleName
            }).length > 0) && self.isAuthorizedForCurrentPlant();
    }

    self.isAuthorizedForDowntimeByRole = function () {
      return isAuthorizedByRoleList(DOWNTIME_ENTRY_ALLOWED_ROLE_IDS);
    }

    self.isAuthorizedForDowntime = function () {
      return self.isAuthorizedForDowntimeByRole() || self.isHotEndOperator();
    };

    self.isAuthorizedForHourlyDowntime = function () {
      return isAuthorizedByRoleList(HOURLY_DOWNTIME_ROLE_IDS);
    };

    self.isAuthorizedForQualifications = function () {
      var loc = self.getSelectedRoleLocation();
      return self.isOperator() && loc.role.roleLocation !== apsEnums.Location.None;
    };

    self.isAuthorizedForBatchSummary = function () {
      return self.isAdmin() || !!_.find(self.getRoles(), x => x.roleName=='Commodities and Batch Summary');
    };


    self.getCountInterval = function () {
      var userId = self.getUserId();
      var data = $window.localStorage[userId + countIntervalKey];
      if (data) {
        return JSON.parse(data);
      } else {
        return apsDisplayEnums.CountDisplayInterval.Last24Hours;
      }
    };

    self.setCountInterval = function (countInterval)  {
      var userId = self.getUserId();
      $window.localStorage[userId + countIntervalKey] = countInterval;
    };
  })
  .name;


// WEBPACK FOOTER //
// ./src/app/services/auth/authData.service.ts